import _ from 'lodash';
import * as moment from 'moment';
import angular from 'angular';
import BaseComponent from '../../../../base/component';
import { Inject } from '../../../../base/decorators';
import UtilsService from '../../../../../services/utilsService';

export class EmployerRepActivateComponent extends BaseComponent {
    @Inject('$stateParams') $stateParams;

    @Inject('$localStorage') $localStorage;

    @Inject('$cookies') $cookies;

    @Inject('AuthService') authService;

    @Inject('EmployerRepService') employerRepService;

    @Inject('PositionService') positionService;

    @Inject('EMPLOYER_REP_AUTH_FLOW') EMPLOYER_REP_AUTH_FLOW;

    @Inject('EMPLOYER_WORKFLOW_STATUS') EMPLOYER_WORKFLOW_STATUS;

    @Inject('EnvSettings') EnvSettings;

    @Inject('WebAppRouteService') WebAppRouteService;

    @Inject('EmployerService') employerService;

    async activateEmployerRep(employerRepId, positionId, employerRepActionType) {
        try {
            UtilsService.cookiesOverride(this.$cookies);
            const successUrl = this.$cookies.get('activationSuccessUrl');
            if (successUrl) {
                this.$cookies.remove('activationSuccessUrl', { domain: window.location.hostname.split('.').slice(-2).join('.') });
            }
            await this.employerRepService.activateNewEmployerRep(employerRepId, { isEmployerRepSignUp: true });
            const { result: employerRep } = await this.employerRepService.getExistingEmployerRep(employerRepId);

            const { result: employer } = await this.employerService.getEmployer(employerRep.employerId);

            const userData = UtilsService.parseJSON(this.$cookies.get('userData')) || {};

            const isRemoteHost = !(window.location.hostname.indexOf('localhost') > -1);

            const expiresInSec = 60 * 60 * 24;
            const authTokenExpiresOnDate = moment().add(expiresInSec, 's').toDate();

            const options = {
                domain: window.location.hostname.split('.').slice(-2).join('.'), // set only domain zone and domain 1 level
                secure: isRemoteHost,
                samesite: 'strict',
                expires: authTokenExpiresOnDate,
            };

            this.$cookies.put('userData', JSON.stringify({
                ...userData,
                roles: ['EM'],
            }), options);

            if (successUrl) {
                this.WebAppRouteService.openPage(successUrl);
                return;
            }

            if (employer?.settings?.isOnlyFastRatingAllowed && !_.includes(employerRep?.roles || [], 'EM_REP_ADMIN')) {
                const employerPart = `employer/${employerRep.employerId}/`;
                this.WebAppRouteService.openPage(`${this.EnvSettings.WebAppUrl}/${employerPart}successful-activation-page`);
                return;
            }

            if (employerRepActionType && positionId) {
                const isUserEmployerRepToDiversity = employerRepActionType === this.EMPLOYER_REP_AUTH_FLOW.DIVERSITY;
                const isUserEmployerRepToFeedBack = employerRepActionType === this.EMPLOYER_REP_AUTH_FLOW.FEEDBACK;
                const isUserEmployerRepToSendToEmployer = employerRepActionType === this.EMPLOYER_REP_AUTH_FLOW.SUBMIT_TO_EMPLOYER;
                const isUserEmployerRepToCompleteJobDefinition = employerRepActionType === this.EMPLOYER_REP_AUTH_FLOW.COMPLETE_JOB_DEFINITION_UPLOAD_JOB_WIZARD;

                if (isUserEmployerRepToFeedBack) {
                    const opts = {
                        positionId,
                        employerId: employerRep.employerId,
                    };
                    this.$localStorage.employerRepReturnUrlParams = null;

                    if (!opts.employerId || !opts.positionId) {
                        throw new Error('employerId or (and) positionId is not provided for redirection to feedback wizard page');
                    }

                    return this.WebAppRouteService.goToEmployerCandidatesPage({
                        employerId: employerRep.employerId,
                        positionId,
                        workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
                    });
                }

                if (isUserEmployerRepToDiversity) {
                    const opts = {
                        positionId: this.$localStorage.employerRepReturnUrlParams.positionId,
                        employerId: this.$localStorage.employerRepReturnUrlParams.employerId,
                    };
                    this.$localStorage.employerRepReturnUrlParams = null;

                    if (!opts.employerId || !opts.positionId) {
                        throw new Error('employerId or (and) positionId is not provided for redirection to diversity optimizer page');
                    }

                    return this.WebAppRouteService.goToDiversityOptimizerPage(opts);
                }

                if (isUserEmployerRepToSendToEmployer) {
                    this.$localStorage.employerRepReturnUrlParams = null;

                    return this.WebAppRouteService.goToEmployerCandidatesPage({
                        employerId: employerRep.employerId,
                        positionId,
                        workflowStep: this.EMPLOYER_WORKFLOW_STATUS.REVIEWED,
                    });
                }

                if (isUserEmployerRepToCompleteJobDefinition) {
                    const { stepName } = await this.positionService.getUploadJobCurrentWizardStep(positionId);

                    return this.WebAppRouteService.goToViewUploadWizardPage({
                        employerId: employerRep.employerId,
                        positionId,
                        stepName,
                    });
                }

                return this.WebAppRouteService.goToEmployerCandidatesPage({
                    employerId: employerRep.employerId,
                    positionId,
                    workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
                });
            }

            if (positionId && !employerRepActionType) {
                const opts = {
                    positionId,
                    employerId: employerRep.employerId,
                    workflowStep: this.EMPLOYER_WORKFLOW_STATUS.FEEDBACK,
                };
                this.$localStorage.employerRepReturnUrlParams = null;

                if (!opts.employerId || !opts.positionId) {
                    throw new Error('employerId or (and) positionId is not provided for redirection to feedback wizard page');
                }

                return this.WebAppRouteService.goToEmployerCandidatesPage(opts);
            }

            window.location.hash = '#/default';
        } catch (error) {
            this.logger.error(error);
            this.authService.logout();
        }
    }

    componentDataWillSetup() {
        UtilsService.cookiesOverride(this.$cookies);
        const isUserAuthenticated = this.$cookies.get('authResult');
        if (!isUserAuthenticated) {
            return this.$state.go('logout');
        }
    }

    async setupComponentData() {
        const { employerRepId } = this.$stateParams;
        const positionId = _.get(this, '$localStorage.employerRepReturnUrlParams.positionId');
        const actionType = _.get(this, '$localStorage.employerRepReturnUrlParams.actionType');

        if (!employerRepId && !positionId) {
            return this.$state.go('logout');
        }

        return this.activateEmployerRep(employerRepId, positionId, actionType);
    }
}

export const EmployerRepActivateComponentConfiguration = {
    template: '<tft-spinner></tft-spinner>',
    controller: EmployerRepActivateComponent,
};

angular.module('TALENYA-FE').component('employerRepActivate', EmployerRepActivateComponentConfiguration);
